import React from 'react';
import { motion } from 'framer-motion';

const techStack = [
  'React',
  'Next.js',
  'Node.js',
  'Express',
  'Python',
  'MongoDB',
  'PostgreSQL',
  'Tailwind',
  'TypeScript',
  'AWS',
  'OpenAI APIs',
  'Framer Motion'
];

function About() {
  return (
    <section id="about" className="section-space bg-snow/50" data-aos="fade-up">
      <div className="section-wrap grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-4"
        >
          <p className="meta-label">05 / The Persona</p>
          <h2 className="mt-4 font-display text-[2.5rem] font-bold uppercase leading-[0.92] tracking-[-0.03em] sm:text-5xl md:text-6xl">
            Builder.
            <br />
            Dreamer.
            <br />
            <span className="italic text-accent">Friend.</span>
          </h2>

          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-ink/15 bg-snow/70 px-4 py-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/75">
              Currently @ KOKO AI
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.66, ease: [0.22, 1, 0.36, 1], delay: 0.06 }}
          className="md:col-span-8"
        >
          <p className="max-w-3xl text-xl leading-snug tracking-[-0.02em] text-ink/88 sm:text-2xl md:text-[2.1rem] md:leading-tight">
            I am <span className="font-semibold">Nithin Sai Choudray</span> — a Software Development Engineer at{' '}
            <span className="font-semibold text-accent">KOKO AI</span>, an EdTech startup reimagining how people
            learn. I live for the moment an idea turns into something real, something that genuinely helps someone.
          </p>

          <p className="copy-refined mt-6 max-w-3xl text-sm text-ink/74 md:text-base">
            By day I ship product at KOKO AI — building learning experiences that feel less like software and more
            like a friend nudging you forward. By night, I freelance, mentor, and obsess over craft. Code, design,
            AI, infrastructure — I have spent years dancing across the full stack, and honestly, I still get
            excited every time something clicks.
          </p>

          <p className="copy-refined mt-5 max-w-3xl text-sm text-ink/74 md:text-base">
            My bigger dream is a community — a real one — where developers, designers, students, and founders help
            each other without ego, share what they know, ask the dumb questions out loud, and grow together. I am
            working on it, and{' '}
            <span className="font-semibold text-ink">I will be launching it soon</span>. Until then, if you are
            building something, stuck on something, or just want a teammate to think with — I am happy to help.
            Always.
          </p>

          <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-ink/12 bg-snow/70 p-3.5 pr-5">
            <span className="rounded-xl bg-coal px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
              Coming Soon
            </span>
            <span className="text-sm text-ink/78">
              <span className="font-semibold">The Community</span> — a space to learn, build, and lift each other up.
            </span>
          </div>

          <div className="mt-10">
            <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-ink">Tech I Live In</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {techStack.map((tech, i) => (
                <motion.span
                  key={tech}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.4, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
                  className="ui-chip rounded-full border border-ink/15 bg-snow/70 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/75"
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-9 md:grid-cols-3 md:gap-10">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-ink">Engineering</h3>
              <p className="copy-refined mt-4 text-sm text-ink/70">
                Engineering, to me, is trust. Stable systems, clean APIs, scalable architecture — so your product
                feels reliable from day one to day one thousand.
              </p>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-ink">Design</h3>
              <p className="copy-refined mt-4 text-sm text-ink/70">
                Design is where I add emotion. Typography, spacing, motion — the small things that make a user
                pause, smile, and trust what they are looking at.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-ink">Community</h3>
                <span className="rounded-full border border-accent/40 bg-accent/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-ink/75">
                  Soon
                </span>
              </div>
              <p className="copy-refined mt-4 text-sm text-ink/70">
                A space where curious people lift each other up — no gatekeeping, no ego. Launching soon. If this
                excites you, reach out — I would love to have you in early.
              </p>
            </div>
          </div>

          <motion.a
            href="#contact"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="ui-button-dark mt-12 inline-flex items-center gap-3 rounded-full border border-ink bg-coal px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
          >
            Get early community access
            <span className="ui-arrow-hover text-accent">↗</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

export default About;
